import React, { useState } from "react";
import AttendanceAnalysis from "./AttendanceAnalysis";

// Accept the API arrays as props
export default function HrDashboard({
  presentData = [],
  absentData = [],
  leaveData = [],
  baseUrl,
  company_code_from_local_storage,
}) {
  const [activeModal, setActiveModal] = useState(null);

  // States for expandable punch logs
  const [expandedEmpId, setExpandedEmpId] = useState(null);
  const [punchData, setPunchData] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const cards = [
    {
      id: "present",
      title: "Present Employees",
      count: presentData.length,
      bg: "bg-emerald-50 border-emerald-200 text-emerald-700",
      badge: "bg-emerald-500",
      data: presentData,
    },
    {
      id: "absent",
      title: "Absent Employees",
      count: absentData.length,
      bg: "bg-rose-50 border-rose-200 text-rose-700",
      badge: "bg-rose-500",
      data: absentData,
    },
    {
      id: "leave",
      title: "On Leave",
      count: leaveData.length,
      bg: "bg-amber-50 border-amber-200 text-amber-700",
      badge: "bg-amber-500",
      data: leaveData,
    },
  ];

  const activeGroup = cards.find((c) => c.id === activeModal);

  // Safely close modal and reset expandable sub-states
  const handleCloseModal = () => {
    setActiveModal(null);
    setExpandedEmpId(null);
    setPunchData([]);
  };

  // Triggered when clicking "View Details"
  const handleViewDetails = async (empId) => {
    if (expandedEmpId === empId) {
      setExpandedEmpId(null);
      return;
    }

    setExpandedEmpId(empId);
    setIsLoadingDetails(true);
    setPunchData([]);

    try {
      const today = new Date();
      let todayDate = today.toISOString().split("T")[0];
      let toDate = todayDate;
      const response = await fetch(
        `${baseUrl}/api/CRMAttendanceDetail/${company_code_from_local_storage}/${empId}/${toDate}`
      );
      if (!response.ok) throw new Error("Failed to fetch attendance data");
      const data = await response.json();
      setPunchData(data);
    } catch (error) {
      console.error("Error fetching punch details:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto font-sans">
      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => setActiveModal(card.id)}
            className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md active:scale-95 text-left outline-none ${card.bg}`}
          >
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase opacity-80">
                {card.title}
              </p>
              <p className="text-3xl font-bold mt-1">{card.count}</p>
            </div>
            <div
              className={`w-3 h-3 rounded-full ${card.badge} animate-pulse`}
            />
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4">
        <AttendanceAnalysis baseUrl={baseUrl} company_code_from_local_storage={company_code_from_local_storage}/>
      </div>

      {/* Popup Modal */}
      {activeGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/40 backdrop-blur-xs transition-opacity">
          {/* Backdrop */}
          <div className="absolute inset-0" onClick={handleCloseModal} />

          {/* Modal Card - Optimized Height for Mobile */}
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden max-h-[70vh] sm:max-h-[80vh] flex flex-col z-10 relative border border-gray-100">
            {/* Header - Shrunk padding */}
            <div className="px-3.5 py-2.5 border-b flex items-center justify-between bg-white">
              <div className="flex items-baseline gap-1.5">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900">
                  {activeGroup.title}
                </h3>
                <span className="text-[10px] sm:text-xs font-medium px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                  {activeGroup.count}
                </span>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg p-1 transition-colors"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body Content - Ultra tight scrolling container */}
            <div className="p-2 sm:p-3 overflow-y-auto space-y-1.5 bg-gray-50/50 flex-1">
              {activeGroup.data.length === 0 ? (
                <p className="text-center text-xs text-gray-400 py-4">
                  No records found.
                </p>
              ) : (
                activeGroup.data.map((emp, idx) => {
                  const isExpanded = expandedEmpId === emp.EmpId;
                  return (
                    <div
                      key={emp.EmpId || idx}
                      className="bg-white rounded-lg border border-gray-100 shadow-xs flex flex-col hover:border-gray-200 transition-colors overflow-hidden"
                    >
                      {/* Main Employee Core Container */}
                      <div className="p-2 flex items-center gap-2.5">
                        {/* Avatar - Smaller for mobile */}
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-semibold text-[11px] shrink-0 uppercase tracking-wider">
                          {emp.Name ? emp.Name.charAt(0) : "E"}
                        </div>

                        {/* Main Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <h4 className="font-medium text-[11px] sm:text-xs text-gray-900 truncate">
                              {emp.Name}
                            </h4>
                            <span className="text-[9px] font-mono text-gray-400 shrink-0">
                              #{emp.EmpId}
                            </span>
                          </div>

                          {/* Secondary Row */}
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            {emp.JobTitle ? (
                              <p className="text-[10px] sm:text-[11px] text-gray-500 truncate">
                                {emp.JobTitle}
                              </p>
                            ) : (
                              <div />
                            )}

                            {/* Dynamic Status Badges & Actions */}
                            <div className="flex items-center gap-1.5 shrink-0">
                              {emp.status === "P" && emp.IN_TIME && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-100">
                                  <span className="w-1 h-1 rounded-full bg-emerald-500" />
                                  {new Date(emp.IN_TIME).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" }
                                  )}
                                </span>
                              )}

                              {emp.LeaveType && (
                                <span className="text-[9px] font-medium px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-100">
                                  {emp.LeaveType}
                                </span>
                              )}

                              {emp.status == "P" && (
                                <button
                                  onClick={() => handleViewDetails(emp.EmpId)}
                                  className={`text-[10px] font-medium px-2 py-0.5 rounded transition-colors ${
                                    isExpanded
                                      ? "bg-gray-200 text-gray-700"
                                      : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                                  }`}
                                >
                                  {isExpanded ? "Hide" : "View Details"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Panel */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50/70 px-3 py-2 text-[11px]">
                          {isLoadingDetails ? (
                            <div className="flex items-center justify-center py-2 text-gray-400 gap-1.5">
                              <div className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                              <span>Loading...</span>
                            </div>
                          ) : punchData.length === 0 ? (
                            <p className="text-gray-400 text-center py-1">
                              No history logs found today.
                            </p>
                          ) : (
                            <div className="space-y-1.5">
                              <div className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                                Punch History
                              </div>
                              {punchData.map((punch) => (
                                <div
                                  key={punch.id}
                                  className="bg-white border border-gray-100 p-1.5 rounded flex items-center justify-between gap-2 shadow-xs"
                                >
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`font-bold text-[9px] px-1 py-0.5 rounded ${
                                        punch.type === "IN"
                                          ? "bg-emerald-100 text-emerald-800"
                                          : "bg-rose-100 text-rose-800"
                                      }`}
                                    >
                                      {punch.type}
                                    </span>
                                    <span className="font-mono text-gray-700">
                                      {new Date(
                                        punch.punch_time
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                      })}
                                    </span>
                                  </div>

                                  {emp.status == "P"
                                    ? punch.latitude &&
                                      punch.longitude && (
                                        <a
                                          href={`https://www.google.com/maps/search/?api=1&query=${punch.latitude},${punch.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 px-1.5 py-0.5 rounded transition-colors shrink-0 cursor-pointer"
                                          title="View on Google Maps"
                                        >
                                          {/* Clean Map Marker SVG */}
                                          <svg
                                            className="w-3.5 h-3.5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                          </svg>
                                          <span>View in Map</span>
                                        </a>
                                      )
                                    : null}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}